import type { CSSProperties, ReactNode } from "react";
import { useEmbedTokens } from "../theme";

export type EmptyIcon =
  | "orders" | "products" | "stores" | "mids" | "referrals"
  | "credits" | "members" | "notifications" | "addresses" | "passkeys";

// 20px outline glyphs on a 24 grid, stroke only so they take the text colour.
const PATHS: Record<EmptyIcon, string> = {
  orders: "M6 2h12l2 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7l2-5zM4 7h16M9 11a3 3 0 0 0 6 0",
  products: "M21 8l-9-5-9 5v8l9 5 9-5V8zM3 8l9 5 9-5M12 13v8",
  stores: "M3 9l1-5h16l1 5M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 12v8h14v-8M10 20v-5h4v5",
  mids: "M3 6h18v12H3zM3 10h18M7 15h4",
  referrals: "M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM4 21a8 8 0 0 1 16 0M19 5v4M17 7h4",
  credits: "M12 3v18M16 7.5C15 6.5 13.6 6 12 6c-2 0-3.5 1-3.5 2.5S10 11 12 11.5s3.5 1.5 3.5 3S14 18 12 18c-1.6 0-3-.5-4-1.5",
  members: "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 20a7 7 0 0 1 14 0M16 4.5a3.5 3.5 0 0 1 0 6.5M18 14a7 7 0 0 1 4 6",
  notifications: "M6 9a6 6 0 0 1 12 0c0 6 2 7 2 7H4s2-1 2-7M10 20a2 2 0 0 0 4 0",
  addresses: "M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
  passkeys: "M8 14a4 4 0 1 1 3.9-4.9M11.9 9.1H21v3h-2.5v2.5H16V12.1h-4.1M7 11h.01",
};

interface Props { icon: EmptyIcon; title: string; body?: string; action?: ReactNode }

/** An icon, one sentence, and the next action, for a list with nothing in it yet. */
export function EmbedEmptyState({ icon, title, body, action }: Props) {
  const t = useEmbedTokens();
  const wrap: CSSProperties = {
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
    gap: t.spacing.sm, padding: `${t.spacing.xl}px ${t.spacing.md}px`,
    fontFamily: t.fontFamily,
  };
  return (
    <div style={wrap} data-empty-icon={icon}>
      <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={t.colors.textSecondary}
        strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d={PATHS[icon]} />
      </svg>
      <p style={{ margin: 0, color: t.colors.text, fontSize: t.fontSize.md, fontWeight: t.fontWeight.medium }}>{title}</p>
      {body && <p style={{ margin: 0, color: t.colors.textSecondary, fontSize: t.fontSize.sm, lineHeight: t.lineHeight.normal, maxWidth: 420 }}>{body}</p>}
      {action && <div style={{ marginTop: t.spacing.xs }}>{action}</div>}
    </div>
  );
}
